import { useEffect, useState } from "react";
import { toast } from "sonner";

interface EmailForwardingSheetProps {
  isOpen: boolean;
  onClose: () => void;
  address: string;
  /** Only true when opened via "Manage" on an already-connected row. */
  allowDisconnect: boolean;
  onDisconnect: () => void;
}

/**
 * Minimal bottom sheet shown from both the "Set up" and "Manage" actions
 * on the Connected Services > Email forwarding row. Same slide/fade pattern
 * as PhotoImportSheet — natural content height, not the full-height shared
 * BottomSheet (there's not enough content here to justify that).
 */
const EmailForwardingSheet = ({
  isOpen,
  onClose,
  address,
  allowDisconnect,
  onDisconnect,
}: EmailForwardingSheetProps) => {
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    } else {
      setShown(false);
      setConfirmingDisconnect(false);
      const id = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  if (!mounted) return null;

  const copyAddress = () => {
    navigator.clipboard.writeText(address)
      .then(() => toast.success("Copied!"))
      .catch(() => toast.error("Couldn't copy"));
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center"
      style={{
        backgroundColor: shown ? "rgba(28,26,62,0.5)" : "rgba(28,26,62,0)",
        transition: "background-color 300ms ease-out",
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Email forwarding"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          maxWidth: 560,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: 20,
          paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          transform: shown ? "translateY(0)" : "translateY(100%)",
          transition: "transform 300ms ease-out",
          boxShadow: "0 -8px 30px rgba(28,26,62,0.18)",
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 999,
            backgroundColor: "#C8C8F4",
            margin: "0 auto 16px",
          }}
          aria-hidden
        />

        <p className="font-display" style={{ fontSize: 18, color: "#1C1A3E", lineHeight: 1.25 }}>
          Email forwarding 💌
        </p>
        <p className="font-body" style={{ fontSize: 13, color: "#54525C", marginTop: 6, lineHeight: 1.5 }}>
          Travel confirmations build trips, school emails import to your schedule, and receipts feed Restock Reminders (a Kin Pro feature). Using Gmail? Just paste the address in the To field when you forward.
        </p>

        <button
          onClick={copyAddress}
          style={{
            display: "block", width: "100%", textAlign: "left",
            background: "#F0F0FF", border: "1.5px solid #C8C8F4", borderRadius: 12,
            padding: "9px 13px", cursor: "pointer", marginTop: 14,
          }}
        >
          <p className="font-body" style={{ fontSize: 11, color: "#54525C", margin: 0, marginBottom: 2 }}>Kin's email 💌</p>
          <p style={{ fontFamily: "monospace", fontSize: 13, color: "#5050C8", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {address}
          </p>
        </button>
        <p className="font-body" style={{ fontSize: 11, color: "#9090A8", marginTop: 4 }}>Tap to copy</p>

        {allowDisconnect && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #E8E6F4" }}>
            {confirmingDisconnect ? (
              <div>
                <p className="font-body" style={{ fontSize: 12.5, color: "#54525C", lineHeight: 1.5 }}>
                  This address stops working and you'll get a brand new one next time. Sure?
                </p>
                <div className="flex gap-3" style={{ marginTop: 10 }}>
                  <button
                    onClick={() => setConfirmingDisconnect(false)}
                    className="font-body font-semibold text-sm"
                    style={{ color: "#54525C" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onDisconnect}
                    className="font-body font-semibold text-sm"
                    style={{ color: "#DC2626" }}
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmingDisconnect(true)}
                className="font-body font-semibold text-sm"
                style={{ color: "#54525C" }}
              >
                Disconnect
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailForwardingSheet;
